import React, { useState, useEffect } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { getMyOrders, cancelMyOrder, updateMyOrder } from '@/lib/api';
import { 
  ShoppingBag, 
  Package, 
  Clock, 
  CheckCircle2, 
  Truck, 
  XCircle,
  MapPin,
  Loader2,
  AlertTriangle,
  Edit,
  X,
  Store,
  Phone,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const getOrderStatuses = (isUSA) => ({
  pendiente: { label: isUSA ? 'Pending' : 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmado: { label: isUSA ? 'Confirmed' : 'Confirmado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
  en_produccion: { label: isUSA ? 'In production' : 'En producción', color: 'bg-purple-100 text-purple-800', icon: Package },
  enviado: { label: isUSA ? 'Shipped' : 'Enviado', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  entregado: { label: isUSA ? 'Delivered' : 'Entregado', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  cancelado: { label: isUSA ? 'Cancelled' : 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle }
});

const MyStoreOrders = () => {
  const { isUSA } = useLocale();
  const ORDER_STATUSES = getOrderStatuses(isUSA);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editForm, setEditForm] = useState({
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    customer_phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const data = await getMyOrders();
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(error.message || (isUSA ? 'Error loading orders' : 'Error al cargar pedidos'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;
    setUpdating(true);
    try {
      await cancelMyOrder(selectedOrder.order_id);
      toast.success(isUSA ? 'Order cancelled' : 'Pedido cancelado');
      fetchOrders();
      setShowCancelDialog(false);
      setSelectedOrder(null);
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error cancelling order' : 'Error al cancelar pedido'));
    } finally {
      setUpdating(false);
    }
  };

  const handleEditOrder = async () => {
    if (!selectedOrder) return;
    setUpdating(true);
    try {
      await updateMyOrder(selectedOrder.order_id, editForm);
      toast.success(isUSA ? 'Order updated' : 'Pedido actualizado');
      fetchOrders();
      setShowEditDialog(false);
      setSelectedOrder(null);
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error updating order' : 'Error al actualizar pedido'));
    } finally {
      setUpdating(false);
    }
  };

  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setEditForm({
      shipping_address: order.shipping_address || '',
      shipping_city: order.shipping_city || '',
      shipping_state: order.shipping_state || '',
      customer_phone: order.customer_phone || '',
      notes: order.notes || ''
    });
    setShowEditDialog(true);
  };

  const openCancelDialog = (order) => {
    setSelectedOrder(order);
    setShowCancelDialog(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(isUSA ? 'en-US' : 'es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price) return isUSA ? 'Quote pending' : 'Por cotizar';
    return `$${price.toLocaleString(isUSA ? 'en-US' : 'es-MX')} MXN`;
  };

  const canModify = (order) => order.status === 'pendiente';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div data-testid="my-store-orders-page">
      <div className="page-header mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShoppingBag className="w-6 h-6 text-emerald-600" />
          {isUSA ? 'My Store Orders' : 'Mis Pedidos de Tiendas'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isUSA ? 'View and manage your orders from online stores' : 'Ve y gestiona tus pedidos de tiendas en línea'}
        </p>
      </div>

      {orders.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <ShoppingBag className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-semibold text-slate-700 mb-2">
              {isUSA ? "You haven't placed any orders yet" : 'Aún no has realizado pedidos'}
            </h3>
            <p className="text-slate-500 text-sm">
              {isUSA ? 'Your orders from online stores will appear here' : 'Tus pedidos de tiendas en línea aparecerán aquí'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const status = ORDER_STATUSES[order.status] || ORDER_STATUSES.pendiente;
            const StatusIcon = status.icon;
            const modifiable = canModify(order);

            return (
              <Card key={order.order_id} className="overflow-hidden" data-testid={`order-${order.order_id}`}>
                <CardContent className="p-0">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 border-b">
                    <div className="flex items-center gap-3">
                      <Store className="w-5 h-5 text-emerald-600" />
                      <div>
                        <span className="font-semibold">{order.store_name}</span>
                        <span className="text-xs text-slate-400 ml-2">#{order.order_id.slice(-8)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">{formatDate(order.created_at)}</span>
                      <Badge className={status.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {status.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Items */}
                    <div className="space-y-2 mb-4">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-slate-400" />
                            <span>{item.product_name}</span>
                            {item.variant_name && (
                              <span className="text-slate-400">({item.variant_name})</span>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="font-medium">{item.quantity} {item.unit}</span>
                            {item.unit_price && (
                              <span className="text-slate-400 ml-2">${item.unit_price.toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Shipping info */}
                    <div className="flex items-start gap-2 text-sm text-slate-600 mb-4 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <p>{order.shipping_address}</p>
                        <p>{order.shipping_city}, {order.shipping_state}</p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <span className="text-lg font-bold text-emerald-600">
                        {formatPrice(order.subtotal)}
                      </span>

                      <div className="flex gap-2">
                        {modifiable ? (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openEditDialog(order)}
                              data-testid={`edit-order-${order.order_id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              {isUSA ? 'Edit' : 'Editar'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => openCancelDialog(order)}
                              data-testid={`cancel-order-${order.order_id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              {isUSA ? 'Cancel' : 'Cancelar'}
                            </Button>
                          </>
                        ) : order.status !== 'cancelado' && order.status !== 'entregado' ? (
                          <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                            <AlertTriangle className="w-4 h-4" />
                            <span>
                              {isUSA 
                                ? 'To modify this order, contact the seller' 
                                : 'Para modificar este pedido, contacta al vendedor'}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isUSA ? 'Edit Order' : 'Editar Pedido'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isUSA ? 'Shipping Address' : 'Dirección de envío'}</Label>
              <Input
                value={editForm.shipping_address}
                onChange={(e) => setEditForm({ ...editForm, shipping_address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isUSA ? 'City' : 'Ciudad'}</Label>
                <Input
                  value={editForm.shipping_city}
                  onChange={(e) => setEditForm({ ...editForm, shipping_city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'State' : 'Estado'}</Label>
                <Input
                  value={editForm.shipping_state}
                  onChange={(e) => setEditForm({ ...editForm, shipping_state: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{isUSA ? 'Phone' : 'Teléfono'}</Label>
              <Input
                value={editForm.customer_phone}
                onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{isUSA ? 'Notes' : 'Notas'}</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder={isUSA ? 'Special instructions...' : 'Instrucciones especiales...'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {isUSA ? 'Cancel' : 'Cancelar'}
            </Button>
            <Button 
              onClick={handleEditOrder} 
              disabled={updating}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {updating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isUSA ? 'Save Changes' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {isUSA ? 'Cancel Order' : 'Cancelar Pedido'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              {isUSA 
                ? 'Are you sure you want to cancel this order? This action cannot be undone.'
                : '¿Estás seguro de que quieres cancelar este pedido? Esta acción no se puede deshacer.'}
            </p>
            {selectedOrder && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedOrder.store_name}</p>
                <p className="text-sm text-slate-500">
                  {selectedOrder.items?.length} {isUSA ? 'item(s)' : 'artículo(s)'} - {formatPrice(selectedOrder.subtotal)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              {isUSA ? 'Keep Order' : 'Mantener Pedido'}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelOrder} 
              disabled={updating}
            >
              {updating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isUSA ? 'Yes, Cancel Order' : 'Sí, Cancelar Pedido'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyStoreOrders;
